import Moment from 'moment';
import moment from 'moment-timezone';
import { extendMoment } from './moment-range.cjs';

export const momentRange = extendMoment(Moment);

const VANCOUVER_PCT_START = moment.tz(
  '2026-03-08 03:00',
  'YYYY-MM-DD HH:mm',
  'America/Vancouver'
);
const EDMONTON_MDT_START = moment.tz('2026-11-01T08:00:00Z', 'America/Edmonton');

const VANCOUVER_TIMEZONES = new Set(['America/Vancouver', 'Canada/Pacific']);
const EDMONTON_TIMEZONES = new Set(['America/Edmonton', 'Canada/Mountain']);

export const getTimezoneAbbreviation = date => {
  if (VANCOUVER_TIMEZONES.has(date.tz()) && date.isSameOrAfter(VANCOUVER_PCT_START)) return 'PCT';
  if (EDMONTON_TIMEZONES.has(date.tz()) && date.isSameOrAfter(EDMONTON_MDT_START)) return 'MDT';
  return date.format('z');
}

export const getTimezoneAbbreviations = (dates, ranges, timezone) => {
  const selectedDates = dates && dates.length
    ? [...new Set(dates)].sort()
    : [moment.tz(timezone).format('YYYY-MM-DD')];
  const abbreviations = new Set();

  selectedDates.forEach(date => {
    const localDate = moment.tz(date, 'YYYY-MM-DD', timezone);
    const dateRanges = ranges ? ranges.filter(range => !range.date || range.date === date) : [];
    if (!dateRanges.length) {
      abbreviations.add(getTimezoneAbbreviation(localDate.hour(12)));
      return;
    }

    const localRanges = dateRanges.map(range => ({
      startTime: moment.tz(range.startTime, timezone),
      endTime: moment.tz(range.endTime, timezone),
    })).sort((first, second) => first.startTime.valueOf() - second.startTime.valueOf());

    localRanges.forEach(range => {
      const start = localDate.clone().set({
        hour: range.startTime.hour(),
        minute: range.startTime.minute(),
        second: range.startTime.second(),
        millisecond: range.startTime.millisecond(),
      });
      const end = localDate.clone().set({
        hour: range.endTime.hour(),
        minute: range.endTime.minute(),
        second: range.endTime.second(),
        millisecond: range.endTime.millisecond(),
      });
      if (!end.isAfter(start)) end.add(1, 'day');

      abbreviations.add(getTimezoneAbbreviation(start));
      abbreviations.add(getTimezoneAbbreviation(end));
    });
  });

  return [...abbreviations];
}

export const formatWithTimezoneAbbreviation = (date, format) =>
  date.format(format.replace(/\bz\b/g, `[${getTimezoneAbbreviation(date)}]`));

export const createRange = (start, end) =>
  momentRange.range(start, end)

export const overlaps = (expectedRange, game, options) => {
  const range = createRange(
    moment.tz(game.startTime, game.timezone).toDate(),
    moment.tz(game.endTime, game.timezone).toDate()
  );
  return range.overlaps(expectedRange, options)
}

export const areConsecutiveGames = (game, otherGame) => {
  const gameRange = createRange(
    moment.tz(game.startTime, game.timezone).toDate(),
    moment.tz(game.endTime, game.timezone).toDate()
  );

  const consecutiveRange = createRange(
    moment.tz(game.startTime, game.timezone).subtract(1, 'hour').toDate(),
    moment.tz(game.endTime, game.timezone).add(1, 'hour').toDate()
  );

  if (overlaps(gameRange, otherGame)) return false;
  return overlaps(consecutiveRange, otherGame, { adjacent: true })
}

export const isSameTime = (first, second) => {
  if (!first || !second) return false;
  const isStartSame = moment.tz(first.startTime, first.timezone).isSame(moment.tz(second.startTime, second.timezone));
  const isEndSame = moment.tz(first.endTime, first.timezone).isSame(moment.tz(second.endTime, second.timezone));
  return isStartSame && isEndSame;
}

export const validateConstraintRanges = (constraint, games, date, homeTeamId, awayTeamId) => {
  const { limit, period } = constraint;
  const violations = [];

  for (let index = 0; index < period; index++) {
    const startDate = moment.utc(date).subtract((period - 1) - index, 'days').format('YYYY-MM-DD')
    const endDate = moment.utc(date).add(index, 'days').format('YYYY-MM-DD');
    const rangeGames = games.filter(game => moment.utc(game.date).isSameOrAfter(startDate, 'day') && moment.utc(game.date).isSameOrBefore(endDate, 'day'))

    const homeTeamGames = rangeGames.filter(game => homeTeamId && [game.homeTeamId, game.awayTeamId].includes(homeTeamId));
    if (homeTeamGames.length >= limit) {
      violations.push(
        ...homeTeamGames.map(game => {
          const offenderId = game.homeTeamId === homeTeamId ? game.homeTeamId : game.awayTeamId
          return {
            ...(game.toJSON ? game.toJSON() : game),
            constraint: {
              ...(constraint.toJSON ? constraint.toJSON() : constraint),
              teamId: offenderId,
              total: homeTeamGames.length,
            },
          }
        })
      )
    }

    const awayTeamGames = rangeGames.filter(game => awayTeamId && [game.homeTeamId, game.awayTeamId].includes(awayTeamId));
    if (awayTeamGames.length >= limit) {
      violations.push(
        ...awayTeamGames.map(game => {
          const offenderId = game.awayTeamId === awayTeamId ? game.awayTeamId : game.homeTeamId
          return {
            ...(game.toJSON ? game.toJSON() : game),
            constraint: {
              ...(constraint.toJSON ? constraint.toJSON() : constraint),
              teamId: offenderId,
              total: homeTeamGames.length,
            },
          }
        })
      )
    }
  }

  return violations
}
